/**
 * pages/SearchPage.tsx
 * Full-screen search experience.
 * Accessible via /search or when user clicks search from dashboard.
 * Awaits: searchApi (backend — store handles mock vs real)
 */

import React, { useEffect, useRef, useState } from 'react';
import { Search, X, ArrowLeft, Filter, FileText } from 'lucide-react';
import { useStore } from '../store';
import { useAuth } from '../hooks';
import { GlobalSpin, Spinner, EmptyState, Badge } from '../components/ui';
import type { Note } from '../types';

const C = {
  amber100:'#FFF4E0', amber200:'#FFD88A', amber300:'#F5A623',
  amber400:'#C4811A', amber500:'#8B5A0A',
  ink:'#1A1814', inkLight:'#3D3A34', muted:'#7A7570',
  border:'#E8E4DC', surface:'#FAFAF8', white:'#FFFFFF',
};
const font = { head:"'Sora',sans-serif", body:"'DM Sans',sans-serif" };

// ─── Highlighted snippet ──────────────────────────────────────────────────────
const Snippet: React.FC<{ html: string }> = ({ html }) => (
  <p
    style={{ fontSize:13, color:C.muted, lineHeight:1.65, fontFamily:font.body }}
    dangerouslySetInnerHTML={{ __html: html.replace(/<mark>/g, `<mark style="background:#FFD88A;color:#8B5A0A;border-radius:2px;padding:0 2px;font-weight:700;">`).replace(/<\/mark>/g,'</mark>') }}
  />
);

// ─── Result Card ──────────────────────────────────────────────────────────────
const ResultCard: React.FC<{ note: Note; snippet: string; matchCount: number; query: string }> = ({ note, snippet, matchCount, query }) => {
  const folders     = useStore(s => s.folders);
  const setOpenNote = useStore(s => s.setOpenNote);
  const folder = folders.find(f => f.id === note.folderId);

  const open = () => {
    setOpenNote(note.id);
    window.location.href = '/dashboard';
  };

  return (
    <div onClick={open} style={{
      background:C.white, border:`1px solid ${C.border}`, borderRadius:14,
      padding:'18px 22px', cursor:'pointer', transition:'all .15s', marginBottom:10,
    }}
      onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.borderColor=C.amber300; (e.currentTarget as HTMLDivElement).style.boxShadow=`0 4px 16px rgba(0,0,0,0.07)`; (e.currentTarget as HTMLDivElement).style.transform='translateY(-1px)'; }}
      onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.borderColor=C.border; (e.currentTarget as HTMLDivElement).style.boxShadow='none'; (e.currentTarget as HTMLDivElement).style.transform=''; }}
    >
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:12, marginBottom:8 }}>
        <div style={{ display:'flex', alignItems:'center', gap:10, minWidth:0 }}>
          <FileText size={15} color={C.amber400} style={{ flexShrink:0 }}/>
          <h3 style={{ fontFamily:font.head, fontWeight:700, fontSize:15, color:C.ink, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{note.title}</h3>
        </div>
        <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:20, background:C.amber100, color:C.amber500, fontFamily:font.body, flexShrink:0, whiteSpace:'nowrap' }}>
          {matchCount} match{matchCount!==1?'es':''}
        </span>
      </div>

      <Snippet html={snippet}/>

      <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:12, flexWrap:'wrap' }}>
        {folder && <Badge color={folder.color}>{folder.name}</Badge>}
        {note.tags.slice(0,3).map(t => <Badge key={t} color={C.amber400}>#{t}</Badge>)}
        <span style={{ marginLeft:'auto', fontSize:11, color:C.muted, fontFamily:font.body }}>
          {new Date(note.updatedAt).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'})}
        </span>
      </div>
    </div>
  );
};

// ─── PAGE ─────────────────────────────────────────────────────────────────────
const SearchPage: React.FC = () => {
  const { authLoading, isAuthenticated } = useAuth();
  const folders        = useStore(s => s.folders);
  const searchResults  = useStore(s => s.searchResults);
  const searchLoading  = useStore(s => s.searchLoading);
  const searchQuery    = useStore(s => s.searchQuery);
  const search      = useStore(s => s.search);
  const clearSearch = useStore(s => s.clearSearch);
  const loadFolders = useStore(s => s.loadFolders);
  const loadNotes   = useStore(s => s.loadNotes);

  const [query, setQuery]           = useState(searchQuery || '');
  const [scopeFolder, setScopeFolder] = useState<string>('all');
  const inputRef = useRef<HTMLInputElement>(null);
  const timer    = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => { loadFolders(); loadNotes(); inputRef.current?.focus(); }, []); // eslint-disable-line

  // Auth gate — after all hooks
  if (authLoading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:'100vh', background:'#FAFAF8', flexDirection:'column', gap:16 }}>
      <div style={{ width:40, height:40, borderRadius:'50%', border:'3px solid #FAEEDA', borderTopColor:'#F5A623', animation:'spin .7s linear infinite' }}/>
      <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
    </div>
  );
  if (!isAuthenticated) { window.location.replace('/login'); return null; }

  const handleChange = (val: string) => {
    setQuery(val);
    clearTimeout(timer.current);
    if (!val.trim()) { clearSearch(); return; }
    timer.current = setTimeout(() => {
      search(val, scopeFolder !== 'all' ? scopeFolder : null, null);
    }, 280);
  };

  const handleScopeChange = (fid: string) => {
    setScopeFolder(fid);
    if (query.trim()) search(query, fid !== 'all' ? fid : null, null);
  };

  if (authLoading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:'100vh' }}><Spinner size={32}/></div>
  );

  return (
    <>
      <GlobalSpin/>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@600;700;800&family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&display=swap');
        *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
        body{font-family:'DM Sans',sans-serif;background:${C.surface};color:${C.ink};}
        input,select{-webkit-font-smoothing:antialiased;}
      `}</style>

      <div style={{ minHeight:'100vh', background:C.surface }}>
        {/* Search hero bar */}
        <div style={{ background:C.white, borderBottom:`1px solid ${C.border}`, padding:'0 24px' }}>
          <div style={{ maxWidth:760, margin:'0 auto', height:72, display:'flex', alignItems:'center', gap:14 }}>
            <a href="/dashboard" style={{ color:C.muted, display:'flex', flexShrink:0 }}><ArrowLeft size={18}/></a>

            {/* Main search input */}
            <div style={{ flex:1, position:'relative' }}>
              <Search size={18} color={C.amber400} style={{ position:'absolute', left:14, top:'50%', transform:'translateY(-50%)', pointerEvents:'none' }}/>
              <input
                ref={inputRef}
                value={query}
                onChange={e => handleChange(e.target.value)}
                placeholder="Search all notes…"
                style={{
                  width:'100%', height:46, padding:'0 44px 0 46px',
                  borderRadius:12, border:`2px solid ${query ? C.amber300 : C.border}`,
                  fontFamily:font.body, fontSize:16, color:C.ink, outline:'none',
                  background: query ? C.amber100 : C.white, transition:'all .15s',
                  boxShadow: query ? `0 0 0 3px ${C.amber300}22` : 'none',
                }}
              />
              {query && (
                <button onClick={() => { setQuery(''); clearSearch(); inputRef.current?.focus(); }}
                  style={{ position:'absolute', right:12, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:C.muted, display:'flex', padding:4 }}>
                  <X size={16}/>
                </button>
              )}
            </div>
          </div>
        </div>

        <div style={{ maxWidth:760, margin:'0 auto', padding:'24px 24px 60px' }}>
          {/* Scope filter */}
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:20, flexWrap:'wrap' }}>
            <Filter size={13} color={C.muted}/>
            <span style={{ fontSize:12, fontWeight:600, color:C.muted, fontFamily:font.body }}>Search in:</span>
            {[{ id:'all', label:'All notes' }, ...folders.map(f => ({ id:f.id, label:f.name }))].map(opt => (
              <button key={opt.id} onClick={() => handleScopeChange(opt.id)} style={{
                padding:'5px 12px', borderRadius:20, border:`1.5px solid ${scopeFolder===opt.id?C.amber300:C.border}`,
                background: scopeFolder===opt.id ? C.amber100 : C.white,
                color: scopeFolder===opt.id ? C.amber500 : C.muted,
                fontWeight: scopeFolder===opt.id ? 700 : 500,
                fontSize:12, fontFamily:font.body, cursor:'pointer', transition:'all .12s',
              }}>{opt.label}</button>
            ))}
          </div>

          {/* Results */}
          {searchLoading ? (
            <Spinner/>
          ) : !query.trim() ? (
            <EmptyState
              icon={<Search size={22}/>}
              title="Search your notes"
              desc="Type any word, formula, name, or phrase — Jötr finds it across every note you've ever written."
            />
          ) : searchResults.length === 0 ? (
            <EmptyState
              icon={<Search size={22}/>}
              title={`No results for "${query}"`}
              desc="Try a different word, check spelling, or broaden your search scope."
            />
          ) : (
            <>
              <p style={{ fontSize:13, color:C.muted, fontFamily:font.body, marginBottom:16, fontWeight:500 }}>
                <span style={{ color:C.ink, fontWeight:700 }}>{searchResults.length}</span> result{searchResults.length!==1?'s':''} for{' '}
                "<span style={{ color:C.amber400, fontWeight:700 }}>{query}</span>"
                {scopeFolder !== 'all' && <> in <span style={{ color:C.amber400, fontWeight:700 }}>{folders.find(f=>f.id===scopeFolder)?.name}</span></>}
              </p>
              {searchResults.map(r => (
                <ResultCard key={r.note.id} note={r.note} snippet={r.snippet} matchCount={r.matchCount} query={query}/>
              ))}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default SearchPage;
